import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getPropuestaDetail } from '@/api/clientes';
import type { CompromisoConContexto, Tag } from '@/types';
import { useEstadoChange } from '@/hooks/useEstadoChange';
import { EstadoSelect } from './EstadoSelect';
import { EstadoModal } from './EstadoModal';
import { EventosHistorial } from './EventosHistorial';
import styles from './CompromisoQuickViewDrawer.module.css';

interface Props {
  open: boolean;
  compromiso: CompromisoConContexto | null;
  clienteSlug: string;
  clienteColor: string;
  tags: Tag[];
  onClose: () => void;
}

/**
 * Drawer de "quick view" para inspeccionar y editar estado de los items de
 * un compromiso sin perder el contexto de la vista consolidada de pendientes.
 *
 * - Cierre con ESC o clic en backdrop.
 * - Cambio de estado abre modal (comentario obligatorio).
 * - Botón "Ver propuesta completa →" navega con hash deep-link, igual que
 *   el comportamiento original del click en la card.
 */
export function CompromisoQuickViewDrawer({
  open,
  compromiso,
  clienteSlug,
  clienteColor,
  tags,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const tagsById = useMemo(() => {
    const m = new Map<string, Tag>();
    for (const t of tags) m.set(t.id, t);
    return m;
  }, [tags]);

  // ESC cierra
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset expansión al cambiar de compromiso
  useEffect(() => {
    setExpanded(new Set());
  }, [compromiso?.id]);

  const propuestaId = compromiso?.propuesta?.id || '';

  // Cargar detalle completo de la propuesta para obtener los hitos del compromiso
  const { data: detail, isLoading } = useQuery({
    queryKey: ['propuesta-detail', propuestaId],
    queryFn: () => getPropuestaDetail(propuestaId),
    enabled: open && !!propuestaId,
  });

  // Hook de cambio de estado — invalida también la lista consolidada del cliente
  const { change, pending, submit, cancel, isSubmitting } = useEstadoChange(
    propuestaId,
    [['cliente-compromisos', clienteSlug]],
  );

  const hitos = useMemo(() => {
    if (!detail || !compromiso) return [];
    return detail.hitos.filter(
      (h) => h.parentType === 'compromiso' && h.parentId === compromiso.id,
    );
  }, [detail, compromiso]);

  if (!open || !compromiso) return null;

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const accent = compromiso.flags.hasBloqueado ? '#dc2626' : clienteColor;
  const pct = compromiso.hitos.pct;
  const fillColor =
    pct >= 80
      ? 'var(--st-done)'
      : pct >= 30
        ? 'var(--brand)'
        : 'var(--st-pend)';

  function irAPropuestaCompleta() {
    if (!compromiso || !compromiso.facility || !compromiso.propuesta) return;
    navigate(
      `/clientes/${clienteSlug}/${compromiso.facility.slug}/${compromiso.propuesta.id}#compromiso-${compromiso.id}`,
    );
    onClose();
  }

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <aside
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="qv-drawer-title"
      >
        <div className={styles.header}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={styles.contextRow}>
              {compromiso.facility && (
                <span
                  className={styles.contextPill}
                  style={{ background: clienteColor + '22', color: clienteColor, borderColor: 'transparent' }}
                >
                  🏭 {compromiso.facility.nombre}
                </span>
              )}
              {compromiso.propuesta && (
                <span className={styles.contextPill}>
                  📄 {compromiso.propuesta.codigo} · {compromiso.propuesta.nombre}
                </span>
              )}
              {compromiso.iniciativa && (
                <span className={styles.contextPill}>
                  🎯 {compromiso.iniciativa.codigo} · {compromiso.iniciativa.nombre}
                </span>
              )}
            </div>
            <div className={styles.titleRow}>
              <span className={styles.codigoBadge} style={{ background: clienteColor }}>
                {compromiso.codigo}
              </span>
              <span id="qv-drawer-title" className={styles.title}>
                {compromiso.nombre}
              </span>
            </div>
            {compromiso.descripcion && (
              <div className={styles.descripcion}>{compromiso.descripcion}</div>
            )}
            {compromiso.tagIds.length > 0 && (
              <div className={styles.tagsRow}>
                {compromiso.tagIds.map((tid) => {
                  const t = tagsById.get(tid);
                  if (!t) return null;
                  return (
                    <span
                      key={tid}
                      className={styles.tagPill}
                      style={{ background: t.color + '22', color: t.color }}
                    >
                      {t.nombre}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <div className={styles.statsBar}>
          <div>
            <div className={styles.statBig} style={{ color: accent }}>
              {pct}%
            </div>
            <div className={styles.statLabel}>Avance</div>
          </div>
          <div className={styles.barWrap}>
            <div
              className={styles.barFill}
              style={{ width: `${pct}%`, background: fillColor }}
            />
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
              {compromiso.hitos.entregados} / {compromiso.hitos.activos}
            </div>
            <div className={styles.statLabel}>
              ítems
              {compromiso.hitos.suspendidos > 0 && ` (${compromiso.hitos.suspendidos} susp.)`}
              {compromiso.hitos.bloqueados > 0 && ` · ${compromiso.hitos.bloqueados} bloq.`}
            </div>
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.sectionTitle}>
            <span>Items / Equipamiento</span>
            <span style={{ color: 'var(--text-soft)', fontWeight: 500 }}>
              {hitos.length} {hitos.length === 1 ? 'ítem' : 'ítems'}
            </span>
          </div>

          {isLoading ? (
            <div className={styles.emptyState}>Cargando ítems…</div>
          ) : hitos.length === 0 ? (
            <div className={styles.emptyState}>Esta partida no tiene ítems registrados.</div>
          ) : (
            <div className={styles.itemList}>
              {hitos.map((h) => {
                const isBloqueado = h.estado === 'bloqueado';
                const isSuspendido = h.estado === 'suspendido';
                const isEntregado = h.estado === 'entregado';
                const isExpanded = expanded.has(h.id);
                return (
                  <div key={h.id}>
                    <div
                      className={[
                        styles.itemRow,
                        isEntregado ? styles.itemRowDone : '',
                        isBloqueado ? styles.itemRowBloqueado : '',
                        isSuspendido ? styles.itemRowSuspendido : '',
                      ].join(' ')}
                    >
                      <EstadoSelect
                        value={h.estado}
                        disabled={isSubmitting}
                        onChange={(next) =>
                          change({
                            hitoId: h.id,
                            hitoTitulo: h.titulo,
                            prevEstado: h.estado,
                            nextEstado: next,
                          })
                        }
                        ariaLabel={`Estado de ${h.titulo}`}
                      />
                      <span className={styles.itemTitulo}>{h.titulo}</span>
                      <button
                        type="button"
                        className={styles.historialBtn}
                        onClick={() => toggleExpand(h.id)}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? '▾' : '▸'} historial
                      </button>
                    </div>
                    {isExpanded && <EventosHistorial hitoId={h.id} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.btnDanger} onClick={onClose}>
            Cerrar
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={irAPropuestaCompleta}
          >
            Ver propuesta completa →
          </button>
        </div>
      </aside>

      <EstadoModal
        open={!!pending}
        hitoTitulo={pending?.hitoTitulo ?? ''}
        estadoAnterior={pending?.prevEstado ?? null}
        estadoNuevo={pending?.nextEstado ?? null}
        onCancel={cancel}
        onSubmit={submit}
        submitting={isSubmitting}
      />
    </>
  );
}
